<script setup lang="ts">
import { p__ } from '@@/bits/intl'
import { getPostPropertyFormattedValue } from '@@/bits/post_properties'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Post, PostCustomPropertyValue, WallCustomPostProperty } from '@@/types'
import SurfacePostPropertiesEmailFieldValue from '@@/vuecomponents/SurfacePostPropertiesEmailFieldValue.vue'
import SurfacePostPropertiesLinkButton from '@@/vuecomponents/SurfacePostPropertiesLinkButton.vue'
import SurfacePostPropertiesPhoneNumberFieldValue from '@@/vuecomponents/SurfacePostPropertiesPhoneNumberFieldValue.vue'
import SurfacePostPropertiesTextFieldValue from '@@/vuecomponents/SurfacePostPropertiesTextFieldValue.vue'
import SurfacePostPropertiesUrlFieldValue from '@@/vuecomponents/SurfacePostPropertiesUrlFieldValue.vue'
import SurfacePostPropertiesUserFieldValue from '@@/vuecomponents/SurfacePostPropertiesUserFieldValue.vue'
import { asyncComputed } from '@vueuse/core'
import { computed } from 'vue'

const props = defineProps<{
  post: Post
  property: WallCustomPostProperty
  darkMode: boolean
}>()

const emit = defineEmits<{
  (e: 'copy-value', { value, propertyName }: { value: string; propertyName: string }): void
  (e: 'click', { event, value, propertyName }: { event: PointerEvent; value: string; propertyName: string }): void
}>()

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

const POST_BG_COLOR_MAP = {
  blue: ['bg-oceanic-200', 'bg-oceanic-800'],
  green: ['bg-park-200', 'bg-park-800'],
  orange: ['bg-canary-200', 'bg-tangerine-800'],
  purple: ['bg-grape-200', 'bg-grape-800'],
  red: ['bg-scarlet-200', 'bg-scarlet-800'],
}
const singleSelectBackgroundClass = computed(() => {
  const color = surfacePostPropertiesStore.getDisplayedColorForPost(props.post)
  if (!color) {
    return props.darkMode ? 'bg-grey-800' : 'bg-grey-100'
  }

  return props.darkMode ? POST_BG_COLOR_MAP[color][1] : POST_BG_COLOR_MAP[color][0]
})

const customPropertyPrimitiveValue = computed(() => {
  if (!props.post.custom_properties || !props.property.id) return undefined
  return props.post.custom_properties[props.property.id]
})

const customPropertyValue = asyncComputed<string>(async () => {
  if (customPropertyPrimitiveValue.value == null) return ''
  return await getPostPropertyFormattedValue({
    property: props.property,
    value: customPropertyPrimitiveValue.value,
  })
}, '')

function parseLinkButtonUrl(url: string): string | undefined {
  try {
    return new URL(url).href
  } catch (_err) {
    try {
      return new URL(`https://${url}`).href
    } catch (err) {
      return undefined
    }
  }
}

const linkButtonUrl = computed<string | undefined>(() => {
  if (props.property.data_type !== 'link_button' || customPropertyPrimitiveValue.value == null) return undefined
  return parseLinkButtonUrl(customPropertyPrimitiveValue.value as string)
})

const handleCopy = (value: PostCustomPropertyValue) => {
  if (value == null || typeof value !== 'string') return
  emit('copy-value', { value, propertyName: props.property.name })
}

const handleClick = (event: any, value: PostCustomPropertyValue) => {
  if (value == null || typeof value !== 'string') return
  emit('click', { event, value, propertyName: props.property.name })
}
</script>

<template>
  <div
    v-if="property.data_type === 'text' && customPropertyValue"
    :class="[
      'cursor-pointer',
      'focus-visible:ring-2 focus-visible:rounded-lg',
      darkMode ? 'focus-visible:ring-canary-500' : 'focus-visible:ring-grape-500',
    ]"
    tabindex="0"
    :aria-label="
      p__(
        'When clicked, copies value of specific custom property to clipboard',
        'Click to copy %{customPropertyName}',
        { customPropertyName: property.name },
      )
    "
    @click="handleClick($event, customPropertyValue)"
    @keydown.enter.prevent="handleCopy(customPropertyValue)"
    @keydown.space.prevent="handleCopy(customPropertyValue)"
  >
    <SurfacePostPropertiesTextFieldValue
      :post-property="{
        id: property.id || '',
        name: property.name,
        formattedValue: customPropertyValue,
        dataType: property.data_type,
      }"
      :dark-mode="darkMode"
      :property-value-class="'text-body-small-posts'"
      as="span"
    />
  </div>
  <SurfacePostPropertiesEmailFieldValue
    v-else-if="property.data_type === 'email' && customPropertyValue"
    :post-property="{
      id: property.id || '',
      name: property.name,
      formattedValue: customPropertyValue,
      dataType: property.data_type,
    }"
    :dark-mode="darkMode"
    :property-value-class="'text-body-small-posts'"
    as="span"
  />
  <SurfacePostPropertiesUrlFieldValue
    v-else-if="property.data_type === 'url' && customPropertyValue"
    :post-property="{
      id: property.id || '',
      name: property.name,
      formattedValue: customPropertyValue,
      dataType: property.data_type,
    }"
    :dark-mode="darkMode"
    :property-value-class="'text-body-small-posts'"
    as="span"
  />
  <SurfacePostPropertiesPhoneNumberFieldValue
    v-else-if="property.data_type === 'phone_number' && customPropertyValue"
    :post-property="{
      id: property.id || '',
      name: property.name,
      formattedValue: customPropertyValue,
      rawValue: customPropertyPrimitiveValue,
      dataType: property.data_type,
    }"
    :dark-mode="darkMode"
    :property-value-class="'text-body-small-posts'"
    as="span"
  />
  <SurfacePostPropertiesUserFieldValue
    v-else-if="property.data_type === 'user' && customPropertyValue"
    :post-property="{
      id: property.id || '',
      name: property.name,
      formattedValue: customPropertyValue,
      rawValue: customPropertyPrimitiveValue,
      dataType: property.data_type,
    }"
    :dark-mode="darkMode"
    :property-value-class="'text-body-small-posts'"
    as="span"
  />
  <SurfacePostPropertiesLinkButton
    v-else-if="property.data_type === 'link_button' && linkButtonUrl"
    :class="['min-w-0 inline-flex']"
    :name="property.name"
    :url="linkButtonUrl"
    :size="24"
    :dark-mode="darkMode"
  />
  <div
    v-else-if="customPropertyValue"
    :class="[
      'cursor-pointer',
      'focus-visible:ring-2 focus-visible:rounded-lg',
      darkMode ? 'focus-visible:ring-canary-500' : 'focus-visible:ring-grape-500',
      property.data_type === 'number' && 'text-end',
    ]"
    tabindex="0"
    :aria-label="
      p__(
        'When clicked, copies value of specific custom property to clipboard',
        'Click to copy %{customPropertyName}',
        { customPropertyName: property.name },
      )
    "
    @click="handleClick($event, customPropertyValue)"
    @keydown.enter.prevent="handleCopy(customPropertyValue)"
    @keydown.space.prevent="handleCopy(customPropertyValue)"
  >
    <span
      :class="[
        'inline-block',
        'text-body-small-posts',
        darkMode ? 'text-light-text-100' : 'text-dark-text-100',
        property.data_type === 'single_select' && [
          'rounded-lg px-2 py-0.5 my-1 w-fit break-word-anywhere',
          singleSelectBackgroundClass,
        ],
      ]"
    >
      {{ customPropertyValue }}
    </span>
  </div>
</template>
